import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Eye, EyeOff } from "lucide-react";
import {
  meApi,
  meQueryKeys,
  type MfaEnrolResponse,
} from "../../shared/api/me";
import { apiError } from "../../shared/api/client";
import { PageHeader } from "../../shared/ui/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";

/** T79 — Bảo mật tài khoản. Real /api/v1/me + /auth/mfa/*. */
export function MySecurityPage() {
  const qc = useQueryClient();
  const profile = useQuery({
    queryKey: meQueryKeys.profile(),
    queryFn: () => meApi.get(),
  });
  const mfaOn = profile.data?.mfaEnrolled ?? false;

  return (
    <div>
      <PageHeader
        title="Bảo mật tài khoản"
        description="Mật khẩu, xác thực 2 lớp (TOTP), và phiên đăng nhập."
      />

      <div className="space-y-4">
        <ChangePasswordCard />
        <MfaCard
          mfaOn={mfaOn}
          onChanged={() =>
            qc.invalidateQueries({ queryKey: meQueryKeys.profile() })
          }
        />
        <SessionsCard />
      </div>
    </div>
  );
}

// ----------------------------------------------------------- change password

/** Ô nhập mật khẩu kèm icon con mắt để ẩn/hiện. */
function PasswordInput({
  value,
  onChange,
  autoComplete,
  minLength,
}: {
  value: string;
  onChange: (v: string) => void;
  autoComplete: string;
  minLength?: number;
}) {
  const [show, setShow] = useState(false);
  return (
    <div className="relative">
      <Input
        type={show ? "text" : "password"}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        autoComplete={autoComplete}
        minLength={minLength}
        required
        className="pr-10"
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
        title={show ? "Ẩn mật khẩu" : "Hiện mật khẩu"}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
      >
        {show ? (
          <EyeOff className="h-4 w-4" aria-hidden="true" />
        ) : (
          <Eye className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
}

function ChangePasswordCard() {
  const [cur, setCur] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const change = useMutation({
    mutationFn: () => meApi.changePassword(cur, next),
    onSuccess: () => {
      setCur("");
      setNext("");
      setConfirm("");
      setError(null);
      setMsg(
        "Đã đổi mật khẩu. Mọi phiên đăng nhập khác đã bị thoát — hãy đăng nhập lại trên các thiết bị đó."
      );
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle>Đổi mật khẩu</CardTitle>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            setMsg(null);
            setError(null);
            if (next !== confirm) {
              setError("Mật khẩu mới nhập lại không khớp.");
              return;
            }
            change.mutate();
          }}
        >
          <p className="text-sm text-muted-foreground">
            Mật khẩu mới được kiểm tra qua HaveIBeenPwned (k-anonymity) và băm
            Argon2id. Sau khi đổi, tất cả phiên sẽ bị thoát.
          </p>
          <div className="space-y-1.5">
            <Label htmlFor="cur-password">Mật khẩu hiện tại</Label>
            <PasswordInput
              value={cur}
              onChange={setCur}
              autoComplete="current-password"
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="new-password">Mật khẩu mới (tối thiểu 8 ký tự)</Label>
            <PasswordInput
              value={next}
              onChange={setNext}
              autoComplete="new-password"
              minLength={8}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirm-password">Nhập lại mật khẩu mới</Label>
            <PasswordInput
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          {msg && <p className="text-sm text-emerald-600">{msg}</p>}
          <div className="flex justify-end">
            <Button type="submit" disabled={change.isPending}>
              {change.isPending ? "Đang đổi…" : "Đổi mật khẩu"}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------- 2FA MFA

function MfaCard({
  mfaOn,
  onChanged,
}: {
  mfaOn: boolean;
  onChanged: () => void;
}) {
  const [enrol, setEnrol] = useState<MfaEnrolResponse | null>(null);
  const [code, setCode] = useState("");
  const [pwd, setPwd] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: () => meApi.mfaEnrol(),
    onSuccess: (r) => {
      setEnrol(r);
      setError(null);
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });
  const confirm = useMutation({
    mutationFn: () => meApi.mfaConfirm(code),
    onSuccess: () => {
      setEnrol(null);
      setCode("");
      setMsg("Đã bật xác thực 2 lớp.");
      setError(null);
      onChanged();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });
  const disable = useMutation({
    mutationFn: () => meApi.mfaDisable(pwd),
    onSuccess: () => {
      setPwd("");
      setMsg("Đã tắt xác thực 2 lớp.");
      setError(null);
      onChanged();
    },
    onError: (e: unknown) => setError(apiError(e).detail),
  });

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <div className="flex items-center justify-between gap-2">
          <CardTitle>Xác thực 2 lớp (TOTP)</CardTitle>
          <Badge variant={mfaOn ? "success" : "warning"}>
            {mfaOn ? "Đang bật" : "Đang tắt"}
          </Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error && <p className="text-sm text-destructive">{error}</p>}
        {msg && <p className="text-sm text-emerald-600">{msg}</p>}

        {!mfaOn && !enrol && (
          <Button
            type="button"
            onClick={() => {
              setMsg(null);
              start.mutate();
            }}
            disabled={start.isPending}
          >
            {start.isPending ? "Đang tạo…" : "Bật xác thực 2 lớp"}
          </Button>
        )}

        {!mfaOn && enrol && (
          <div className="space-y-2.5">
            <p className="text-sm">
              Quét mã hoặc nhập khoá thủ công vào ứng dụng Authenticator
              (Google/Microsoft Authenticator…), rồi nhập mã 6 số để xác nhận.
            </p>
            <div className="text-sm">
              Khoá thủ công:{" "}
              <code className="select-all rounded bg-muted px-1.5 py-0.5 font-mono">
                {enrol.secret}
              </code>
            </div>
            <div className="break-all text-xs text-muted-foreground">
              otpauth URI: {enrol.otpAuthUri}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mfa-code">Mã xác thực (6 số)</Label>
              <Input
                id="mfa-code"
                inputMode="numeric"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                maxLength={8}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setEnrol(null)}
              >
                Hủy
              </Button>
              <Button
                type="button"
                onClick={() => confirm.mutate()}
                disabled={confirm.isPending || code.length < 6}
              >
                {confirm.isPending ? "Đang xác nhận…" : "Xác nhận bật 2FA"}
              </Button>
            </div>
          </div>
        )}

        {mfaOn && (
          <div className="space-y-2.5">
            <p className="text-sm">
              Tắt 2FA yêu cầu nhập lại mật khẩu hiện tại (thao tác nhạy cảm).
            </p>
            <div className="space-y-1.5">
              <Label htmlFor="mfa-disable-password">Mật khẩu hiện tại</Label>
              <Input
                id="mfa-disable-password"
                type="password"
                value={pwd}
                onChange={(e) => setPwd(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="flex justify-end">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  setMsg(null);
                  disable.mutate();
                }}
                disabled={disable.isPending || !pwd}
              >
                {disable.isPending ? "Đang tắt…" : "Tắt xác thực 2 lớp"}
              </Button>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ------------------------------------------------------------------ sessions

function SessionsCard() {
  const [msg, setMsg] = useState<string | null>(null);
  const revoke = useMutation({
    mutationFn: () => meApi.revokeAllSessions(),
    onSuccess: () =>
      setMsg(
        "Đã thoát mọi phiên đăng nhập. Các thiết bị khác sẽ cần đăng nhập lại."
      ),
  });

  return (
    <Card className="max-w-lg">
      <CardHeader>
        <CardTitle>Phiên đăng nhập</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <p className="text-sm text-muted-foreground">
          Danh sách từng thiết bị đang đăng nhập chưa khả dụng (cần sổ đăng ký
          phiên — sẽ bổ sung sau). Hiện có thể thoát toàn bộ phiên ngay lập tức:
        </p>
        {msg && <p className="text-sm text-emerald-600">{msg}</p>}
        <Button
          type="button"
          variant="secondary"
          onClick={() => {
            setMsg(null);
            if (
              window.confirm(
                "Thoát khỏi tất cả thiết bị đang đăng nhập (kể cả thiết bị này)?"
              )
            )
              revoke.mutate();
          }}
          disabled={revoke.isPending}
        >
          {revoke.isPending ? "Đang thoát…" : "Thoát tất cả thiết bị"}
        </Button>
      </CardContent>
    </Card>
  );
}
